// HU-20 · Preparar la carta de notificación (F-05, Sprint 2; rev. 11, 6 criterios).
//
// LA CARTA ES EL ENVOLTORIO DEL FORMATO AMARILLO. Se prepara con la plantilla vigente de la
// autoridad del país (HU-45: «SENASA · v1»), combinada con los datos del caso (C1), y solo
// cuando el Amarillo está completo: sin él el servidor responde «Completa el Formato
// Amarillo antes de preparar la carta» y aquí se muestra (C2). Sin plantilla cargada, dice
// que falta y a quién pedirla (C4).
//
// PREPARAR ES UN PASO PROPIO (C3). Las acciones que se ofrecen son EXACTAMENTE las que el
// servidor dice que caben en el estado actual (`acciones_de_la_carta`): «preparar» cuando
// no hay carta; «regenerar» y «solicitar firma» cuando la hay; ninguna cuando ya se envió.
// La pantalla no inventa la máquina de estados.
//
// SE VERSIONA (C5/C6). Si el Formato Amarillo cambia después de preparada, el servidor la
// marca «Carta desactualizada: el Formato Amarillo cambió» y aquí aparece el aviso con el
// botón «Regenerar carta»: nace la v2 y la v1 sigue igual. La enviada no se toca.
//
// HU-21 · LA RUTA DE FIRMA LA DECIDE LA AUTORIDAD (RN-05), no el país: donde NO exige PRFV
// (Perú) la acción es «Firmar y enviar» —firmada con el responsable pre-configurado (HU-40)
// y enviada en el mismo acto, C1—; donde lo exige, la única acción es «Solicitar firma del
// PRFV» (C2), la espera es un estado con fecha (C3), la firma se registra con quién y cuándo
// (C5) y presenta OTRA persona (C6). Presentar sin firma no se ejecuta: «La carta no está
// firmada» (C7).
//
// HU-22 · EL CARGO NO SE GENERA, SE RECIBE. Con la carta enviada se sube el acuse que
// devolvió la autoridad («cargo_SENASA.pdf» + fecha) y queda en el expediente como «Cargo de
// la autoridad · fecha» (C1); hasta entonces el trámite dice «Acuse pendiente» / «Acuse
// demorado» (C2/C3).
//
// CC-02 · BLOQUE B11 · UNA CARTA POR AUTORIDAD DESTINO (RN-77, `ui/MG-ILE-1075.md`). El trámite
// tiene un Formato Amarillo y una carta por cada autoridad destino, cada una con su firma, su
// envío, su cargo y su plazo (RN-80). Esta pantalla compone: arriba, «Autoridades a las que se
// notifica» (`DestinosDelTramite`: propuesta del sistema, agregar y quitar con motivo, destinos
// cerrados, RN-74); debajo, un contenedor por carta (`CartaDeAutoridad`). Con UNA sola carta y
// nada que ajustar, la pantalla es exactamente la de antes (AT §11.6).

import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, FileText } from 'lucide-react';
import { Alert, Button, Mono, PageHeader, StatusBadge } from '../components/ui';
import Bandera from '../components/Banderas';
import CartaDeAutoridad from '../components/CartaDeAutoridad';
import DestinosDelTramite, { seMuestranLosDestinos } from '../components/DestinosDelTramite';
import { etiquetaDeLaCarta, verTramite } from '../api/regulatorio';
import type { Tramite } from '../api/regulatorio';
import { cartasActivas } from '../api/cartas';
import type { TramiteConCartas, VistaDeCarta } from '../api/cartas';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';

/** Mientras no hay trámite en pantalla: el fallo que impidió abrirlo, o la espera. */
function SinTramite({ fallo }: Readonly<{ fallo: Fallo | null }>) {
  if (!fallo) return <p className="p-6 text-body text-ink-soft" data-testid="carta-cargando">Abriendo la carta…</p>;
  const sinTramite = fallo.estado === 404;
  return (
    <Alert tone="danger" title={sinTramite ? 'Este caso no tiene trámite regulatorio' : 'No se pudo abrir la carta'} detalleTecnico={fallo.detalleTecnico}
      data-testid={sinTramite ? 'carta-vacio' : 'carta-error'}>
      {fallo.message}
    </Alert>
  );
}

/**
 * Lo que la cabecera dice de la carta. Con una sola: su autoridad, su estado y su versión, como
 * siempre. Con varias: las autoridades destino; el estado de cada carta vive en su contenedor.
 */
function ResumenDeLasCartas({ cartas }: Readonly<{ cartas: VistaDeCarta[] }>) {
  if (cartas.length !== 1) {
    return <>{cartas.map((c) => <StatusBadge key={c.autoridad_codigo ?? ''} tone="info">{c.autoridad_nombre}</StatusBadge>)}</>;
  }
  const [carta] = cartas;
  return (
    <>
      {carta.autoridad_nombre && <StatusBadge tone="info">{carta.autoridad_nombre}</StatusBadge>}
      <StatusBadge tone={carta.estado_carta === 'sin_preparar' ? 'neutral' : 'success'}>{etiquetaDeLaCarta(carta.estado_carta)}</StatusBadge>
      {carta.carta_version > 0 && <StatusBadge tone="info">v{carta.carta_version}</StatusBadge>}
    </>
  );
}

export default function CartaDeNotificacion({ casoId, numero, pais, actor, textoDeVolver = 'Volver al trámite', onVolver, onCambio }: Readonly<{
  casoId: string; numero?: string | null; pais?: string;
  /** Quién presenta / registra (el nombre de la sesión). */
  actor?: string;
  /** «Volver al trámite» cuando se llegó desde él; «Volver a la bandeja» cuando se abrió directo. */
  textoDeVolver?: string;
  onVolver: () => void;
  onCambio?: () => void;
}>) {
  const [tramite, setTramite] = useState<Tramite | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);

  const cargar = useCallback(async () => {
    setFallo(null);
    try {
      setTramite(await verTramite(casoId));
    } catch (e) {
      setFallo(aFallo(e));
    }
  }, [casoId]);

  // `cargar` ya captura sus fallos y los deja en `fallo`: no hay promesa suelta que vigilar.
  useEffect(() => { cargar(); }, [cargar]);

  /** Tras cada acto la pantalla toma el trámite que devolvió el servidor (§8.1 R3). */
  const alCambiar = (t: Tramite) => {
    setTramite(t);
    onCambio?.();
  };

  const cartas = tramite ? cartasActivas(tramite) : [];
  const varias = cartas.length > 1;
  const cabecera = (
    <PageHeader
      eyebrow="Asuntos Regulatorios · Carta de notificación"
      hu="HU-20 · HU-21 · HU-22 · RN-05 / RN-24 / RN-33 · HU-45 · CC-02 RN-74 / RN-77 / RN-80 / RN-81"
      title="Carta de notificación"
      subtitle={(
        <span className="inline-flex flex-wrap items-center gap-2">
          {pais && <Bandera cod={pais} />}
          <Mono className="font-semibold">{numero ?? casoId.slice(0, 8)}</Mono>
          {tramite && <ResumenDeLasCartas cartas={cartas} />}
        </span>
      )}
      icon={FileText}
      actions={<Button variant="secondary" size="sm" icon={ArrowLeft} onClick={onVolver}>{textoDeVolver}</Button>}
    />
  );

  if (!tramite) {
    return <>{cabecera}<SinTramite fallo={fallo} /></>;
  }

  const destinos = (tramite as TramiteConCartas).destinos;
  return (
    <>
      {cabecera}
      {seMuestranLosDestinos(tramite, destinos) && (
        <DestinosDelTramite casoId={casoId} tramite={tramite} destinos={destinos} onTramite={alCambiar} />
      )}
      <div>
        {cartas.map((c) => (
          <CartaDeAutoridad key={c.autoridad_codigo ?? 'unica'} casoId={casoId} tramite={tramite} carta={c} varias={varias}
            actor={actor} onTramite={alCambiar} />
        ))}
      </div>
    </>
  );
}
